import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  Search, Loader2, Eye, CheckCircle2, Clock, AlertCircle, XCircle,
  ChevronLeft, ChevronRight, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export const AdminOrders = () => {
  const { token } = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [cleaning, setCleaning] = useState(false);
  const limit = 20;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          limit: limit.toString(),
          skip: (page * limit).toString()
        });
        if (statusFilter !== 'all') {
          params.append('status', statusFilter);
        }

        const response = await fetch(`${backendUrl}/api/admin/orders?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setOrders(data.orders);
          setTotal(data.total);
        }
      } catch (error) {
        console.error('Orders error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token, backendUrl, page, statusFilter]);

  const getStatusBadge = (status) => {
    const configs = {
      'paid': { class: 'bg-green-500/20 text-green-400', icon: CheckCircle2, label: 'Bezahlt' },
      'pending': { class: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'Ausstehend' },
      'payment_pending': { class: 'bg-blue-500/20 text-blue-400', icon: Clock, label: 'Zahlung läuft' },
      'failed': { class: 'bg-red-500/20 text-red-400', icon: AlertCircle, label: 'Fehlgeschlagen' },
      'cancelled': { class: 'bg-slate-500/20 text-slate-400', icon: XCircle, label: 'Storniert' },
    };
    return configs[status] || configs['pending'];
  };

  const handleCleanup = async () => {
    if (!window.confirm('Alle verwaisten Bestellungen (ohne Cloud-Instanz, älter als 24h) löschen?')) return;
    setCleaning(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/orders/cleanup`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      
      if (response.ok) {
        toast.success(`${data.deleted || 0} Bestellungen, ${data.deleted_plans || 0} Pläne, ${data.deleted_subscriptions || 0} Abos gelöscht`);
        setPage(0);
      } else {
        toast.error(data.detail || `Fehler ${response.status}`);
      }
    } catch (e) {
      console.error('Cleanup error:', e);
      toast.error(`Verbindungsfehler: ${e.message}`);
    } finally {
      setCleaning(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Bestellungen</h1>
          <p className="text-slate-400 mt-1">{total} Bestellungen insgesamt</p>
        </div>
        <Button
          variant="outline"
          onClick={handleCleanup}
          disabled={cleaning}
          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          data-testid="cleanup-orders-btn"
        >
          {cleaning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
          Verwaiste aufräumen
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
                <SelectItem value="payment_pending">Zahlung läuft</SelectItem>
                <SelectItem value="paid">Bezahlt</SelectItem>
                <SelectItem value="failed">Fehlgeschlagen</SelectItem>
                <SelectItem value="cancelled">Storniert</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : orders.length === 0 ? (
            <p className="text-slate-500 text-center py-20">Keine Bestellungen gefunden</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">ID</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Kunde</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Datum</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Betrag</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const statusConfig = getStatusBadge(order.status);
                    const StatusIcon = statusConfig.icon;
                    return (
                      <tr key={order.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-4 px-4">
                          <span className="text-slate-300 font-mono text-sm">
                            {order.id?.substring(0, 8)}...
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-white">{order.customer_name || order.customer_email || 'N/A'}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-slate-400 text-sm">
                            {new Date(order.created_at).toLocaleDateString('de-DE')}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-white font-semibold">
                            {order.total_brutto?.toFixed(2)} €
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={statusConfig.class}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <Button asChild variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                            <Link to={`/admin/orders/${order.id}`}>
                              <Eye className="h-4 w-4 mr-1" /> Details
                            </Link>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700">
              <span className="text-slate-400 text-sm">
                Seite {page + 1} von {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
